import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactPlayer from 'react-player';
import './Player.css';
import VideoComments from './VideoComments';
import Download from './Download';

const Player = () => {
  const { playlistId, videoId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [playlistData, setPlaylistData] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(false);
  const [activeTab, setActiveTab] = useState('playlist'); // 'playlist' or 'comments'
  
  const videoUrl = `https://www.youtube.com/watch?v=${videoId}&rel=0`;

  useEffect(() => {
    if (playlistId) {
      loadPlaylistData();
    } else {
      setActiveTab('comments'); // Default to comments if no playlist
    }
  }, [playlistId]);

  useEffect(() => {
    if (playlistData && videoId) {
      const videoIndex = playlistData.items.findIndex(
        item => item.snippet.resourceId.videoId === videoId
      );
      if (videoIndex !== -1) {
        setCurrentVideoIndex(videoIndex);
      }
    }
  }, [playlistData, videoId]);

  const loadPlaylistData = () => {
    // First check localStorage for saved playlists
    const savedPlaylists = JSON.parse(localStorage.getItem('savedPlaylists') || '[]');
    const savedPlaylist = savedPlaylists.find(playlist => playlist.id === playlistId);
    
    if (savedPlaylist && savedPlaylist.items) {
      setPlaylistData(savedPlaylist);
      return;
    }

    // If not in localStorage, check sessionStorage
    const sessionData = sessionStorage.getItem(`playlist_${playlistId}`);
    if (sessionData) {
      try {
        const parsedData = JSON.parse(sessionData);
        if (parsedData.length > 0) {
          setPlaylistData({
            id: playlistId,
            name: 'Playlist',
            items: parsedData
          });
        }
      } catch (error) {
        console.error('Error parsing session data:', error);
      }
    }
  };

  const handleVideoSelect = (video, index) => {
    const newVideoId = video.snippet.resourceId.videoId;
    setCurrentVideoIndex(index);
    setAutoPlay(true);
    // Navigate to new video while maintaining playlist context
    if (playlistId) {
      navigate(`/Player/${playlistId}/${newVideoId}`);
    } else {
      navigate(`/Player/${newVideoId}`);
    }
  };

  const handleVideoEnd = () => {
    if (playlistData && currentVideoIndex < playlistData.items.length - 1) {
      // Auto-play next video
      const nextVideo = playlistData.items[currentVideoIndex + 1];
      handleVideoSelect(nextVideo, currentVideoIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (playlistData && currentVideoIndex > 0) {
      const prevVideo = playlistData.items[currentVideoIndex - 1];
      handleVideoSelect(prevVideo, currentVideoIndex - 1);
    }
  };

  const handleNext = () => {
    if (playlistData && currentVideoIndex < playlistData.items.length - 1) {
      const nextVideo = playlistData.items[currentVideoIndex + 1];
      handleVideoSelect(nextVideo, currentVideoIndex + 1);
    }
  };

  const getCurrentVideo = () => {
    if (playlistData && playlistData.items[currentVideoIndex]) {
      return playlistData.items[currentVideoIndex];
    }
    return null;
  };

  const currentVideo = getCurrentVideo();

  return (
    <div className="player-wrapper">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p className="loading-text">Loading video...</p>
          </div>
        </div>
      )}
      
      <div className={`player-content ${loading ? 'hidden' : ''}`}>
        {/* Main Video Section */}
        <div className="video-section">
          <div className="video-container">
            <ReactPlayer
              className="react-player"
              url={videoUrl}
              playing={autoPlay}
              controls={true}
              width="100%"
              height="100%"
              onReady={() => {
                setLoading(false);
                setAutoPlay(false);
              }}
              onEnded={handleVideoEnd}
            />
          </div>
          
          {/* Video Info */}
          <div className="video-info">
            {currentVideo ? (
              <>
                <h1 className="video-title">{currentVideo.snippet.title}</h1>
                <div className="video-meta">
                  <span className="channel-name">{currentVideo.snippet.channelTitle}</span>
                </div>
              </>
            ) : (
              <>
                <div className="video-title-skeleton"></div>
                <div className="video-meta-skeleton"></div>
              </>
            )}
            
            {/* Playlist Navigation Controls */}
            {playlistData && (
              <div className="playlist-controls">
                <div className="playlist-info">
                  <h3 className="playlist-name">{playlistData.name}</h3>
                  <span className="playlist-counter">
                    {currentVideoIndex + 1} of {playlistData.items.length}
                  </span>
                </div>
                <div className="playlist-buttons">
                  <button 
                    className="nav-btn prev-btn"
                    onClick={handlePrevious}
                    disabled={currentVideoIndex === 0}
                    title="Previous video"
                  >
                    <i className="fa-solid fa-backward"></i>
                    <span className="btn-text">Previous</span>
                  </button>
                  <button 
                    className="nav-btn next-btn"
                    onClick={handleNext}
                    disabled={currentVideoIndex === playlistData.items.length - 1}
                    title="Next video"
                  >
                    <span className="btn-text">Next</span>
                    <i className="fa-solid fa-forward"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="sidebar">
          {/* Tab Navigation */}
          <div className="tab-navigation">
            {playlistData && (
              <button 
                className={`tab-btn ${activeTab === 'playlist' ? 'active' : ''}`}
                onClick={() => setActiveTab('playlist')}
              >
                <i className="fa-solid fa-list"></i>
                <span>Playlist</span>
                <span className="tab-badge">{playlistData.items.length}</span>
              </button>
            )}
            <button 
              className={`tab-btn ${activeTab === 'comments' ? 'active' : ''}`}
              onClick={() => setActiveTab('comments')}
            >
              <i className="fa-solid fa-comments"></i>
              <span>Comments</span>
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'playlist' && playlistData && (
              <div className="playlist-container">
                <div className="playlist-videos">
                  {playlistData.items.map((video, index) => (
                    <div
                      key={video.snippet.resourceId.videoId}
                      className={`playlist-item ${index === currentVideoIndex ? 'active' : ''}`}
                      onClick={() => handleVideoSelect(video, index)}
                    >
                      <div className="thumbnail-container">
                        <img
                          src={video.snippet.thumbnails.default.url}
                          alt={video.snippet.title}
                          className="thumbnail"
                          loading="lazy"
                        />
                        <div className="video-number">{index + 1}</div>
                        {index === currentVideoIndex && (
                          <div className="play-indicator">
                            <i className="fa-solid fa-play"></i>
                          </div>
                        )}
                      </div>
                      <div className="video-details">
                        <h4 className="video-title">{video.snippet.title}</h4>
                        <p className="channel-name">{video.snippet.channelTitle}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {activeTab === 'comments' && (
              <div className="comments-container">
                <VideoComments videoId={videoId} />
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .player-wrapper {
          min-height: 100vh;
          background: #f8f9fa;
          position: relative;
          margin-top: 50px; /* Adjust to remove top margin */
          margin-bottom: 40px; /* Add bottom margin for spacing */
        }

        .loading-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.95);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .loading-spinner {
          text-align: center;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e3e3e3;
          border-top: 4px solid #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .loading-text {
          color: #666;
          font-size: 16px;
          margin: 0;
        }

        .player-content {
          display: grid;
          grid-template-columns: 1fr 400px;
          gap: 24px;
          max-width: 1400px;
          margin: 0 auto;
          padding: 20px;
          transition: opacity 0.3s ease;
        }

        .player-content.hidden {
          opacity: 0;
        }

        .video-section {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .video-container {
          position: relative;
          width: 100%;
          padding-top: 56.25%; /* 16:9 aspect ratio */
          background: #000;
        }

        .react-player {
          position: absolute;
          top: 0;
          left: 0;
        }

        .video-info {
          padding: 24px;
        }

        .video-title {
          font-size: 24px;
          font-weight: 600;
          line-height: 1.3;
          margin: 0 0 12px 0;
          color: #1a1a1a;
        }

        .video-meta {
          margin-bottom: 20px;
        }

        .channel-name {
          color: #666;
          font-size: 16px;
          font-weight: 500;
        }

        .playlist-controls {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-radius: 12px;
          padding: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 20px;
        }

        .playlist-info h3 {
          font-size: 18px;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0 0 4px 0;
        }

        .playlist-counter {
          color: #666;
          font-size: 14px;
        }

        .playlist-buttons {
          display: flex;
          gap: 12px;
        }

        .nav-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          border: 2px solid #007bff;
          background: white;
          color: #007bff;
          border-radius: 8px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .nav-btn:hover:not(:disabled) {
          background: #007bff;
          color: white;
          transform: translateY(-1px);
        }

        .nav-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .sidebar {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          overflow: hidden;
          max-height: calc(100vh - 40px);
          display: flex;
          flex-direction: column;
        }

        .tab-navigation {
          display: flex;
          background: #f8f9fa;
          border-bottom: 1px solid #dee2e6;
        }

        .tab-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 16px 12px;
          border: none;
          background: transparent;
          color: #666;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
        }

        .tab-btn.active {
          color: #007bff;
          background: white;
        }

        .tab-btn.active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: #007bff;
        }

        .tab-badge {
          background: #007bff;
          color: white;
          font-size: 12px;
          padding: 2px 6px;
          border-radius: 10px;
          min-width: 20px;
          text-align: center;
       
        }

        .tab-content {
          flex: 1;
          overflow: hidden;
        }

        .playlist-container,
        .comments-container {
          height: 100%;
          overflow-y: auto;
        }

        .playlist-videos {
          padding: 8px;
        }

        .playlist-item {
          display: flex;
          gap: 12px;
          padding: 12px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-bottom: 8px;
        }

        .playlist-item:hover {
          background: #f8f9fa;
        }

        .playlist-item.active {
          background: #e3f2fd;
          border: 2px solid #007bff;
        }

        .thumbnail-container {
          position: relative;
          flex-shrink: 0;
        }

        .thumbnail {
          width: 120px;
          height: 68px;
          object-fit: cover;
          border-radius: 6px;
        }

        .video-number {
          position: absolute;
          top: 4px;
          left: 4px;
          background: rgba(0, 0, 0, 0.8);
          color: white;
          font-size: 12px;
          padding: 2px 6px;
          border-radius: 4px;
          font-weight: 500;
        }

        .play-indicator {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(0, 123, 255, 0.9);
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
        }

        .video-details {
          flex: 1;
          min-width: 0;
        }

        .video-details .video-title {
          font-size: 14px;
          font-weight: 600;
          line-height: 1.3;
          margin: 0 0 6px 0;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          color: #1a1a1a;
        }

        .video-details .channel-name {
          font-size: 12px;
          color: #666;
          margin: 0;
        }

        .comments-container {
          padding: 16px;
        }

        /* Skeleton loading */
        .video-title-skeleton {
          height: 28px;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: loading 1.5s infinite;
          border-radius: 4px;
          margin-bottom: 12px;
        }

        .video-meta-skeleton {
          height: 20px;
          width: 200px;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: loading 1.5s infinite;
          border-radius: 4px;
        }

        @keyframes loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        /* Mobile Responsive */
        @media (max-width: 1024px) {
          .player-content {
            grid-template-columns: 1fr;
            gap: 16px;
            padding: 16px;
          }

          .sidebar {
            max-height: 600px;
          }

          .video-title {
            font-size: 20px;
          }

          .playlist-controls {
            flex-direction: column;
            gap: 16px;
            text-align: center;
          }

          .playlist-buttons {
            justify-content: center;
          }
        }

        @media (max-width: 768px) {
          .player-content {
            padding: 12px;
            gap: 12px;
          }

          .video-info {
            padding: 16px;
          }

          .video-title {
            font-size: 18px;
          }

          .thumbnail {
            width: 100px;
            height: 56px;
          }

          .nav-btn .btn-text {
            display: none;
          }

          .tab-btn span:not(.tab-badge) {
            display: none;
          }

          .tab-btn {
            padding: 12px 8px;
          }
        }

        @media (max-width: 480px) {
          .player-content {
            padding: 8px;
          }

          .video-info {
            padding: 12px;
          }

          .playlist-controls {
            padding: 16px;
          }

          .thumbnail {
            width: 80px;
            height: 45px;
          }

          .video-details .video-title {
            font-size: 13px;
          }

          .video-details .channel-name {
            font-size: 11px;
          }
        }
      `}</style>
    </div>
  );
};

export default Player;